import { VpJob } from './vp-job'

/** ISO date string (YYYY-MM-DD) for dates without time. Matches the server's `DateOnly`. */
export type IsoDate = string

/** Toolbar grouping mode for foreman schedule Summary and Crew Schedule tabs. */
export type ForemanScheduleGroupBy = 'division' | 'job'

/** Map key for foremen with no scheduled job in the visible window when grouping by job. */
export const FOREMAN_SCHEDULE_NO_JOB_GROUP_KEY = 'no-job'

export function normalizeForemanScheduleJobId(jobId: string | null | undefined): string {
    return (jobId ?? '').trim()
}

/** Index of each job id in `activeJobs()` order (matches the Job filter dropdown). */
export function buildJobOrderIndex(jobs: VpJob[]): Map<string, number> {
    const map = new Map<string, number>()
    jobs.forEach((job, index) => {
        const id = normalizeForemanScheduleJobId(job.id)
        if (id) map.set(id, index)
    })
    return map
}

export interface ForemanScheduleJobGroupSortKey {
    groupJobId: string | null
}

/** Sort job groups in Job-filter dropdown order; unknown jobs after known; no-job last. */
export function compareForemanScheduleJobGroups(
    a: ForemanScheduleJobGroupSortKey,
    b: ForemanScheduleJobGroupSortKey,
    jobOrderIndex: Map<string, number>,
): number {
    if (!a.groupJobId && b.groupJobId) return 1
    if (a.groupJobId && !b.groupJobId) return -1
    if (!a.groupJobId && !b.groupJobId) return 0

    const idA = a.groupJobId!
    const idB = b.groupJobId!
    const indexA = jobOrderIndex.get(idA)
    const indexB = jobOrderIndex.get(idB)

    if (indexA !== undefined && indexB !== undefined) return indexA - indexB
    if (indexA !== undefined) return -1
    if (indexB !== undefined) return 1

    if (idA > idB) return -1
    if (idA < idB) return 1
    return 0
}

export function addDaysIso(iso: IsoDate, days: number): IsoDate {
    const [y, m, d] = iso.split('-').map(n => parseInt(n, 10))
    const dt = new Date(Date.UTC(y, m - 1, d))
    dt.setUTCDate(dt.getUTCDate() + days)
    return dt.toISOString().slice(0, 10)
}

export function todayIso(): IsoDate {
    return new Date().toISOString().slice(0, 10)
}

/**
 * Coerce to `YYYY-MM-DD` for request JSON. The app's `JsonDateInterceptor` turns API date strings into
 * `Date` instances; `JSON.stringify` then emits `…T00:00:00.000Z`, which does not deserialize into
 * the server's `DateOnly` and the whole body fails binding (null DTO on PUT/POST).
 */
export function isoDateStringForApi(value: string | Date | null | undefined): IsoDate {
    if (value == null) return ''
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? '' : value.toISOString().slice(0, 10)
    }
    const s = String(value).trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
    const m = /^\d{4}-\d{2}-\d{2}/.exec(s)
    return m ? m[0] : ''
}

/** Default forward-looking window for the Crews tab timeline. */
export const CREWS_TIMELINE_DAYS = 90

export interface ForemanCrewsTimeline {
    dateRangeStart: IsoDate
    dateRangeEnd: IsoDate
    foremen: ForemanCrewGroup[]
    holidays: CompanyHoliday[]
}

export interface CompanyHoliday {
    date: IsoDate
    name: string
    description: string | null
    isPartialDay: boolean
}

export interface ForemanCrewGroup {
    hrRef: number
    name: string
    jobTitle: string
    members: CrewMember[]
}

export interface CrewMember {
    hrRef: number
    name: string
    jobTitle: string
    isForeman: boolean
    timeOff: TimeOffEntry[]
}

export interface TimeOffEntry {
    leaveRequestId: number
    startDate: IsoDate
    endDate: IsoDate
    status: 'Approved' | 'Pending'
    leaveType: string
    comment: string | null
    totalHours: number | null
}

/** A single active Vista employee with their Vista-default and effective foreman status. */
export interface ForemanRosterEntry {
    hrRef: number
    name: string
    jobTitle: string
    /** True when Vista's job title denotes this employee as a foreman (the default). */
    isVistaForeman: boolean
    /** Effective foreman status after applying the WebApps override (if any). */
    isForeman: boolean
    /** Division assigned to this foreman, or null/empty for "No Division". */
    divisionId?: string | null
}

/** Visible weekday columns on the Crew Schedule tab (~8 weeks) so crews can be forecast well into the future. */
export const CREW_SCHEDULE_WEEKDAYS = 40

/** @deprecated Use CREW_SCHEDULE_WEEKDAYS */
export const CREW_SCHEDULE_DAYS = CREW_SCHEDULE_WEEKDAYS

/** Context window for PTO/holidays when projecting crew schedules. */
export const CREW_SCHEDULE_CONTEXT_DAYS = 365

export function isWeekendIso(iso: IsoDate): boolean {
    const [y, m, d] = iso.split('-').map(n => parseInt(n, 10))
    const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
    return day === 0 || day === 6
}

/** First weekday on or after the given date. */
export function firstWeekdayOnOrAfter(iso: IsoDate): IsoDate {
    let current = iso
    while (isWeekendIso(current)) {
        current = addDaysIso(current, 1)
    }
    return current
}

/** Next `count` weekdays starting from the first weekday on or after `start`. */
export function collectWeekdaysFrom(start: IsoDate, count: number): IsoDate[] {
    const result: IsoDate[] = []
    let current = firstWeekdayOnOrAfter(start)
    while (result.length < count) {
        if (!isWeekendIso(current)) {
            result.push(current)
        }
        current = addDaysIso(current, 1)
    }
    return result
}

/** Weekend dates (Sat/Sun) strictly between two dates, exclusive of both endpoints. */
export function weekendDatesBetween(start: IsoDate, end: IsoDate): IsoDate[] {
    const result: IsoDate[] = []
    let current = addDaysIso(start, 1)
    while (current < end) {
        if (isWeekendIso(current)) result.push(current)
        current = addDaysIso(current, 1)
    }
    return result
}

export function daysBetweenIso(a: IsoDate, b: IsoDate): number {
    const [y1, m1, d1] = a.split('-').map(n => parseInt(n, 10))
    const [y2, m2, d2] = b.split('-').map(n => parseInt(n, 10))
    const t1 = Date.UTC(y1, m1 - 1, d1)
    const t2 = Date.UTC(y2, m2 - 1, d2)
    return Math.round((t2 - t1) / 86400000)
}

export interface CrewScheduleContext {
    dateRangeStart: IsoDate
    dateRangeEnd: IsoDate
    foremen: CrewScheduleForeman[]
    holidays: CompanyHoliday[]
}

export interface CrewScheduleForeman {
    hrRef: number
    name: string
    jobTitle: string
    /** Division assigned to this foreman, or null/empty for "No Division". */
    divisionId?: string | null
    /** Display name of the assigned division, or null when unassigned. */
    divisionName?: string | null
    members: CrewScheduleMember[]
}

export interface CrewScheduleMember {
    hrRef: number
    name: string
    jobTitle: string
    isForeman: boolean
    timeOff: TimeOffEntry[]
}

export interface JobPhaseOption {
    phase: string
    description: string
    remainingHours: number
}

export interface JobPhaseHours {
    vpJobId: string
    phase: string
    phaseDescription: string
    actualHours: number
    estHours: number
    projHours: number
    remainingHours: number
}

export interface CrewScheduleDayAssignment {
    date: IsoDate
    vpJobId: string
    phase: string
    hoursScheduled: number
    remainingHoursAtStart: number
    remainingHoursAtEnd: number
    availableCrewCount: number
    totalCrewCount: number
    /** Number of distinct crews (foremen) working this same job+phase on this date. */
    sharedCrewCount: number
    isHoliday: boolean
    holidayName: string | null
    /** True when this day was scheduled past the phase's remaining hours via a forced day count. */
    isOverHours: boolean
}

/** A single crew segment definition fed into the global co-projection. */
export interface FullScheduleSegmentInput {
    /** Stable client id used to map projected days back to the originating segment. */
    clientId: string
    foremanHrRef: number
    vpJobId: string
    phase: string
    startDate: IsoDate
    sortOrder: number
    /** When set, the segment stops contributing on/after this date (session-only truncation). */
    endDateExclusive?: IsoDate | null
    /** When set, the assignment occupies exactly this many working days regardless of remaining hours. */
    forcedDays?: number | null
}

export interface ProjectFullCrewScheduleRequest {
    startDate: IsoDate
    maxProjectionDays: number
    hoursPerPerson: number
    segments: FullScheduleSegmentInput[]
    weekendEntries: SavedWeekendEntry[]
    reassignments: {
        memberHrRef: number
        fromForemanHrRef: number
        toForemanHrRef: number
        startDate: IsoDate
        endDate: IsoDate
    }[]
}

export interface ProjectedSegment {
    clientId: string
    vpJobId: string
    phase: string
    startDate: IsoDate
    startingRemainingHours: number
    overThresholdHours: number
    projectedFinishDate: IsoDate | null
    days: CrewScheduleDayAssignment[]
}

export interface ForemanScheduleProjection {
    foremanHrRef: number
    segments: ProjectedSegment[]
}

/** Per job+phase shared pool summary across all crews in a single projection. */
export interface JobPhasePool {
    vpJobId: string
    phase: string
    projHours: number
    estHours: number
    remainingHours: number
    remainingAfterProjection: number
    overThresholdHours: number
    crewCount: number
}

export interface FullCrewScheduleProjection {
    foremen: ForemanScheduleProjection[]
    pools: JobPhasePool[]
}

export interface CrewScheduleSegment {
    id: string
    vpJobId: string
    phase: string
    startDate: IsoDate
    days: CrewScheduleDayAssignment[]
    startingRemainingHours: number
    /** Hours of the projection that fall beyond the estimate (max(proj - est, 0)); drives the per-day red over-estimate flag. */
    overThresholdHours: number
    /** Session-only truncation boundary set by "clear from day"; not persisted. */
    endDateExclusive: IsoDate | null
    projectedFinishDate: IsoDate | null
    /** When set, the assignment occupies exactly this many working days regardless of remaining hours. */
    forcedDays: number | null
}

/** Session-only state per foreman on the Crew Schedule tab. */
export interface CrewScheduleForemanState {
    segments: CrewScheduleSegment[]
    loadingProjection: boolean
    error: string | null
}

export function crewScheduleJobPhaseKey(vpJobId: string, phase: string): string {
    return `${(vpJobId ?? '').trim()}|${(phase ?? '').trim()}`
}

/** Compact persisted segment definition returned by the API; re-projected on load. */
export interface SavedForemanSegment {
    foremanHrRef: number
    vpJobId: string
    phase: string
    startDate: IsoDate
    sortOrder: number
    startingRemainingHours: number
    /** Persisted truncation boundary set by "clear from day"; the segment stops on/after this date. */
    endDateExclusive: IsoDate | null
    /** When set, the assignment occupies exactly this many working days regardless of remaining hours. */
    forcedDays: number | null
}

/** Persisted crew reassignment as stored on the server (Guid id as string). */
export interface SavedReassignment {
    id: string
    memberHrRef: number
    memberName: string
    fromForemanHrRef: number
    fromForemanName: string
    toForemanHrRef: number
    toForemanName: string
    startDate: IsoDate
    endDate: IsoDate
}

/** Manually scheduled weekend (Sat/Sun) work as stored on the server (Guid id as string). */
export interface SavedWeekendEntry {
    id: string
    foremanHrRef: number
    date: IsoDate
    vpJobId: string
    phase: string
    hours: number
}

export interface SavedCrewSchedule {
    segments: SavedForemanSegment[]
    reassignments: SavedReassignment[]
    weekendEntries: SavedWeekendEntry[]
}

/** Session-only temporary crew member reassignment between foremen. */
export interface CrewMemberReassignment {
    id: string
    memberHrRef: number
    memberName: string
    fromForemanHrRef: number
    fromForemanName: string
    toForemanHrRef: number
    toForemanName: string
    startDate: IsoDate
    endDate: IsoDate
}

export function isReassignmentActiveOnDate(reassignment: CrewMemberReassignment, date: IsoDate): boolean {
    return reassignment.startDate <= date && date <= reassignment.endDate
}

export function reassignmentOverlapsExisting(
    memberHrRef: number,
    startDate: IsoDate,
    endDate: IsoDate,
    existing: CrewMemberReassignment[],
    excludeId?: string,
): boolean {
    return existing.some(r => {
        if (excludeId && r.id === excludeId) return false
        if (r.memberHrRef !== memberHrRef) return false
        return startDate <= r.endDate && endDate >= r.startDate
    })
}

/** Minimal view of an existing segment needed for overlap checks. */
export interface ForemanSegmentSpan {
    vpJobId: string
    phase: string
    startDate: IsoDate
    /** Last weekday with projected work, or null when nothing projected yet. */
    lastWorkedDate: IsoDate | null
    endDateExclusive: IsoDate | null
}

export type SegmentOverlapKind = 'duplicate' | 'truncates-existing' | 'truncated-by-existing'

export interface SegmentOverlap {
    kind: SegmentOverlapKind
    vpJobId: string
    phase: string
    startDate: IsoDate
    lastWorkedDate: IsoDate | null
}

function segmentOverlapJobId(jobId: string): string {
    return normalizeForemanScheduleJobId(jobId)
}

function segmentOverlapPhase(phase: string): string {
    return (phase ?? '').trim()
}

function segmentEffectiveEnd(seg: ForemanSegmentSpan): IsoDate {
    return seg.lastWorkedDate ?? seg.startDate
}

/** Detects duplicate assignments and date collisions when adding a new segment. */
export function findSegmentOverlaps(
    existing: ForemanSegmentSpan[],
    newJobId: string,
    newPhase: string,
    newStartDate: IsoDate,
): SegmentOverlap[] {
    const newJob = segmentOverlapJobId(newJobId)
    const newPhaseNorm = segmentOverlapPhase(newPhase)
    if (!newStartDate) return []

    const results: SegmentOverlap[] = []

    for (const seg of existing) {
        const segJob = segmentOverlapJobId(seg.vpJobId)
        const segPhase = segmentOverlapPhase(seg.phase)
        if (newJob && newPhaseNorm && segJob === newJob && segPhase === newPhaseNorm) {
            results.push({
                kind: 'duplicate',
                vpJobId: segJob,
                phase: segPhase,
                startDate: seg.startDate,
                lastWorkedDate: seg.lastWorkedDate,
            })
        }
    }

    for (const seg of existing) {
        if (seg.endDateExclusive && seg.endDateExclusive <= newStartDate) continue

        if (seg.startDate <= newStartDate && segmentEffectiveEnd(seg) >= newStartDate) {
            results.push({
                kind: 'truncates-existing',
                vpJobId: segmentOverlapJobId(seg.vpJobId),
                phase: segmentOverlapPhase(seg.phase),
                startDate: seg.startDate,
                lastWorkedDate: seg.lastWorkedDate,
            })
        }
    }

    const laterSegments = existing
        .filter(seg => seg.startDate > newStartDate)
        .sort((a, b) => a.startDate.localeCompare(b.startDate))

    if (laterSegments.length > 0) {
        const seg = laterSegments[0]
        results.push({
            kind: 'truncated-by-existing',
            vpJobId: segmentOverlapJobId(seg.vpJobId),
            phase: segmentOverlapPhase(seg.phase),
            startDate: seg.startDate,
            lastWorkedDate: seg.lastWorkedDate,
        })
    }

    return results
}

/** One-line advisory sentence, or null when there is no overlap. */
export function describeSegmentOverlaps(
    overlaps: SegmentOverlap[],
    formatDate: (iso: IsoDate) => string,
): string | null {
    if (!overlaps.length) return null

    const duplicate = overlaps.find(o => o.kind === 'duplicate')
    if (duplicate) {
        return `Already assigned to ${duplicate.vpJobId} / ${duplicate.phase}.`
    }

    const truncates = overlaps.find(o => o.kind === 'truncates-existing')
    if (truncates) {
        const through = formatDate(truncates.lastWorkedDate ?? truncates.startDate)
        return `Already on ${truncates.vpJobId} / ${truncates.phase} through ${through} — starting here will end that assignment early.`
    }

    const cutShort = overlaps.find(o => o.kind === 'truncated-by-existing')
    if (cutShort) {
        return `${cutShort.vpJobId} / ${cutShort.phase} starts ${formatDate(cutShort.startDate)} — this assignment may end early.`
    }

    return null
}

/** Short overlap label for narrow grid cells; full detail belongs in a tooltip. */
export function compactSegmentOverlapNote(overlaps: SegmentOverlap[]): string | null {
    if (!overlaps.length) return null

    const duplicate = overlaps.find(o => o.kind === 'duplicate')
    if (duplicate) return 'Duplicate assignment'

    const truncates = overlaps.find(o => o.kind === 'truncates-existing')
    if (truncates) return `Cuts ${truncates.vpJobId} short`

    const cutShort = overlaps.find(o => o.kind === 'truncated-by-existing')
    if (cutShort) return `Cut short by ${cutShort.vpJobId}`

    return null
}

interface CrewMemberLike {
    hrRef: number
    isForeman: boolean
}

interface ForemanWithMembersLike<T extends CrewMemberLike> {
    hrRef: number
    members: T[]
}

/** Effective roster for a foreman on a date after temporary reassignments. */
export function resolveEffectiveMembers<T extends CrewMemberLike>(
    foreman: ForemanWithMembersLike<T>,
    date: IsoDate,
    allForemen: ForemanWithMembersLike<T>[],
    reassignments: CrewMemberReassignment[],
): T[] {
    const memberByHrRef = new Map<number, T>()
    for (const f of allForemen) {
        for (const m of f.members) {
            if (!memberByHrRef.has(m.hrRef)) memberByHrRef.set(m.hrRef, m)
        }
    }

    const effective = foreman.members.filter(
        m =>
            m.isForeman ||
            !reassignments.some(
                r =>
                    r.memberHrRef === m.hrRef &&
                    r.fromForemanHrRef === foreman.hrRef &&
                    isReassignmentActiveOnDate(r, date),
            ),
    )

    for (const r of reassignments) {
        if (r.toForemanHrRef !== foreman.hrRef || !isReassignmentActiveOnDate(r, date)) continue
        const member = memberByHrRef.get(r.memberHrRef)
        if (!member || member.isForeman) continue
        if (!effective.some(m => m.hrRef === member.hrRef)) effective.push(member)
    }

    return effective
}
